import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { FilterDigestService } from '@zaoblako/web/core/services/personalization/filter-digest.service';
import { HttpParamsHelpersService } from '@zaoblako/web/core/services/http-params-helpers.service';
import {
  buildDigestApiPayload,
  pickDigestApiOverlayParams,
  shouldUseDigestForApiQuery,
  stripDigestApiInternalKeys
} from '@zaoblako/web/core/utils/digest-api-query-params.util';

import { Observable, catchError, map, of } from 'rxjs';

const DOCUMENT_REGISTRY_API_PREFIX = '/document/registry';
const DIGEST_AWARE_API_ROUTES = new Set([
  '/finance/plan-fact/report/payment-outgoing/items',
  '/finance/document/abc-analysis/items',
  '/finance/document/abc-analysis/total',
  '/finance/brs/payment-calendar/planned-income',
  '/finance/brs/payment-calendar/planned-income/total'
]);

export interface DigestAwareApiQueryOptions {
  synchronizeDigest?: boolean;
}

@Injectable({ providedIn: 'root' })
export class DigestAwareApiQueryService {
  private readonly router = inject(Router);
  private readonly filterDigestService = inject(FilterDigestService);
  private readonly httpParamsHelpersService = inject(HttpParamsHelpersService);

  public prepare(
    apiUrl: string,
    queryParams: object,
    options?: DigestAwareApiQueryOptions
  ): Observable<Record<string, unknown>> {
    const sanitizedQuery = stripDigestApiInternalKeys(queryParams as Record<string, unknown>);
    const digest = this.getRouteDigest();
    const serializedQuery = this.httpParamsHelpersService.objectToHttpParams(sanitizedQuery).toString();

    if (!this.isDigestAwareApiUrl(apiUrl) || !digest || !shouldUseDigestForApiQuery(serializedQuery)) {
      return of(sanitizedQuery);
    }

    const digestQuery = {
      digest,
      ...pickDigestApiOverlayParams(sanitizedQuery)
    };

    if (options?.synchronizeDigest === false) {
      return of(digestQuery);
    }

    return this.filterDigestService.updateOne(digest, buildDigestApiPayload(sanitizedQuery)).pipe(
      map(() => digestQuery),
      catchError(() => of(sanitizedQuery))
    );
  }

  private getRouteDigest(): string | null {
    const digest = this.router.parseUrl(this.router.url).queryParams.digest;
    return typeof digest === 'string' && digest.length ? digest : null;
  }

  private isDigestAwareApiUrl(apiUrl: string): boolean {
    return apiUrl === DOCUMENT_REGISTRY_API_PREFIX
      || apiUrl.startsWith(`${DOCUMENT_REGISTRY_API_PREFIX}/`)
      || DIGEST_AWARE_API_ROUTES.has(apiUrl);
  }
}
